import { Component, forwardRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { PrFormSelectListComponent } from '@app/components/pr-form/pr-form-select-list/pr-form-select-list.component';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PrSubformComponent } from '@app/components/pr-form/pr-subform/pr-subform.component';
import { PrTableFormComponent } from '@app/components/pr-form/pr-table-form/pr-table-form.component';
import { PrTableColumn } from '@app/components/pr-table/pr-table.component';
import { AssetDao } from '@app/dao/asset/asset.dao';
import { ProductDao } from '@app/dao/product/product.dao';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { UserDao } from '@app/dao/user/user.dao';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, Observable, Subject, debounceTime, map, mergeMap, tap } from 'rxjs';
import { PrPerFormBuilder } from './ea-per-form.builder';

interface searchCriteria {
  supportTypes: string[];
}
@Component({
    selector: 'ea-per-form',
    templateUrl: './ea-per-form.component.html',
    styleUrls: ['./ea-per-form.component.scss'],
    providers: [
        {
            provide: PrFormAbstract,
            useExisting: forwardRef(() => EaPerFormComponent),
        },
    ],
    standalone: false
})
export class EaPerFormComponent extends PrPerFormBuilder implements OnInit {
  _ = _;
  doneSubject: Subject<void> = new Subject<void>();
  tabChanged: BehaviorSubject<any> = new BehaviorSubject(null);
  changeTab: BehaviorSubject<any> = new BehaviorSubject(null);
  userId;

  criteriaForm: UntypedFormGroup;

  products: any[] = [];
  supports: any[] = [];
  supportTypes = [];
  supportsLength = 0;
  supportsPageSize = 15;
  currentPage = 0;
  hideTable: any;

  @ViewChild('tableForm', { static: true }) tableForm: PrTableFormComponent;
  @ViewChild('supportsForm', { static: true }) supportsForm: PrSubformComponent;
  @ViewChild('formSelectList', {
    static: true,
    read: PrFormSelectListComponent,
  })
  formSelectList: PrFormSelectListComponent;

  iframe;

  defaultAllocationFormValue: any;

  columnsDefinition = [
    new PrTableColumn('', 'action', 'action'),
    new PrTableColumn('Supports', 'name', { span: 3 }),
    new PrTableColumn('Repartition', 'allocationPercentage', 'input', {
      changeCallback: this.tableAllocationColumnValueChanged.bind(this),
      suffix: '%',
      span: 2,
      class: 'allocation-percentage',
      hasTotal: true,
      totalText: true,
    }),
    new PrTableColumn('Montant investi', 'investedAmount', 'currency', {
      span: 2,
      hasTotal: true,
    }),
  ];
  users: any[];
  references: any;
  groupes: any[];
  sources: any[];
  managers: any[];

  constructor(
    protected formConstructor: PrFormConstructor,
    private httpApi: HttpApiService,
    private userDao: UserDao,
    private productsDao: ProductDao,
    private assetDao: AssetDao,
    private referenceDao: ReferenceDao,
  ) {
    super(formConstructor);

    this.criteriaForm = this.formBuilder.group({
      autocomplete: [],
      supportTypes: [],
    });
  }

  ngOnInit(): void {
    if (this.formSelectList) this.formSelectList.loading = true;
    this.loading = true;

    combineLatest([
      this.userDao.getCouple(),
      this.assetDao.getReferences(),
      this.assetDao.getProviders({
        productType: 'PER',
        status: ['VALID'],
      }),
      this.productsDao.getProducts({ type: ['PER'] }),
      this.referenceDao.getSupportTypes(),
      this.referenceDao.getAuthentified(),
    ])
      .pipe(
        map(([users, references, managers, products, supportTypes, user]) => {
          this.users = users;
          this.references = references;
          this.sources = references[0];
          this.managers = managers;
          this.products = _.sortBy(products, 'name');

          this.loading = false;
          this.userId = user.id;
          this.supportTypes = supportTypes;

          this.setDefaultValue();
        }),
      )
      .subscribe(() => {
        if (this.formSelectList) this.formSelectList.loading = false;
      });

    this.criteriaForm.valueChanges
      .pipe(
        tap(() => (this.formSelectList.loading = true)),
        debounceTime(500),
        mergeMap(this.search.bind(this)),
      )
      .subscribe();
  }

  get title() {
    return this.generateTitle("Modifier l'actif", 'Ajout d’un PER');
  }

  generateTitle(editTitle: string, addTitle: string) {
    if (!this.entity || !this.entity.id) return addTitle;
    if (!this.entity.label || this.entity.label === '') return editTitle;
    return `${editTitle} “${this.entity.label}”`;
  }

  setDefaultValue() {
    if (this.entity.label)
      this.form
        .get('product')
        .setValue(_.find(this.products, p => p.name === this.entity.label));
    if (this.entity.owners && this.entity.owners.length)
      this.form.get('owners').setValue(this.buildInitialOwners(this.entity));
  }

  selectType(type) {
    this.form.controls.type.setValue(type);
    this.form.updateValueAndValidity();
    this.assetDao.getProviderByType(type).subscribe(res => {
      this.managers = res;
    });
  }

  // ajout
  openBiIframe(biUrl) {
    this.iframe = document.createElement('iframe');

    this.iframe.src = biUrl.url;
    this.iframe.name = 'euodiaFrame';
    this.iframe.id = 'iframe-bi';
    this.iframe.width = '100%';
    this.iframe.height = '100%';
    this.iframe.onLoad = function () {
      alert(this.contentWindow.location);
    };
    this.iframe.style.border = '0';
    this.iframe = document.querySelector('.bi').appendChild(this.iframe);
    setTimeout(
      () =>
        this.iframe.addEventListener(
          'load',
          this.messageHandler.bind(this),
          true,
        ),
      1000,
    );
  }

  private messageHandler(event: MessageEvent): void {
    this.iframe = undefined;
    this.doneSubject.next();
  }

  changeSupportsPage(event) {
    this.currentPage = event.pageIndex;
    this.formSelectList.loading = true;
    this.search().subscribe();
  }

  search(): Observable<any> {
    return this.httpApi
      .post(`/supports/search`, {
        page: this.currentPage + 1,
        size: this.supportsPageSize,
        lifeInsuranceId: this.options?.productId,
        autocomplete: this.criteriaForm.value.autocomplete,
        supportTypes: this.criteriaForm.value.supportTypes
          ? [this.criteriaForm.value.supportTypes]
          : undefined,
      })
      .pipe(
        map(res => {
          this.formSelectList.loading = false;
          this.supportsLength = res.totalElements;
          this.supports = res.content;
          return res;
        }),
      );
  }

  onSupportSelect(support) {
    let newControl = this.formBuilder.group(
      this.createModel({
        ...support,
        id: null,
        prSupportId: support.id,
      }),
    );
    let formArray = this.form.controls.allocation as UntypedFormArray;
    newControl.setValue({ ...newControl.value, index: formArray.value.length });
    formArray.push(newControl);
  }

  onSupportDeselect(support) {
    let formArray = this.form.controls.allocation as UntypedFormArray;
    let control = formArray.controls.findIndex(
      c => c.value.id == support.id || c.value.isin == support.isin,
    );
    formArray.removeAt(control);
  }

  getSupportTypeValue(support) {
    let matchedType = this.supportTypes.find(type => {
      return type.id === support.type;
    });
    return matchedType?.value;
  }

  getInfoLabelColor(support) {
    switch (support.type) {
      case 'EURO_FOUND':
        return 'euro-info';
      case 'REALESTATE_FOUND':
        return 'estate-info';
      case 'SCI':
        return 'sci-info';
      case 'OPCI':
        return 'opci-info';
      case 'SCPI':
        return 'scpi-info';
      case 'TRACKER_ETF':
        return 'etf-info';
      case 'STRUCTURED_PRODUCTS':
        return 'structured-info';
      case 'ACCOUNT_UNIT_ACTIONS':
        return 'au-actions-info';
      case 'ACCOUNT_UNIT_BOND':
        return 'au-bond-info';
      case 'ACCOUNT_UNIT_MIXED':
        return 'au-mixed-info';
      case 'THEMED_UNIT_MIXED':
        return 't-mixed-info';
      case 'TITRE_VIF':
        return 'titrevif-info';
      case 'PRIVATE_EQUITY':
        return 'prveq-info';
      case 'PILOTED':
        return 'piloted-info';
      default:
        return '';
    }
  }

  tableAllocationColumnValueChanged(value: any, row: any, column: any) {
    this.allocation.controls[row.index].setValue({
      ...this.allocation.controls[row.index].value,
      [column.name]: value / 100,
    });
    this.markAsDirtyTouchedAndRefresh(row.index);
  }

  cancelManageAllocationform() {
    this.entity = this.form.value;
    this.entity.allocation = this.defaultAllocationFormValue.allocation;
    this.generate(this.entity, this.options);
    this.setDefaultValue();
    this.defaultAllocationFormValue = [];
  }

  validManageAllocationform() {
    this.newOptionLogic();
    this.tableForm.initTable();
    this.defaultAllocationFormValue = [];
  }

  markAsDirtyTouchedAndRefresh(control) {
    this.allocation.controls[control].markAsDirty();
    this.allocation.controls[control].markAsTouched();
    this.tableForm.initTable();
  }

  formatTableDataFunction() {
    return data =>
      _(data)
        .map((data, i) => {
          return {
            index: i,
            name: data.name,
            allocationPercentage: data.allocationPercentage * 100,
            investedAmount:
              data.totalInvestedAmount * data.allocationPercentage,
            totalInvestedAmount: data.totalInvestedAmount,
          };
        })
        .value();
  }

  newOptionLogic() {
    //First element at 100%
    if (+this.allocation.controls.length == 1)
      return this.allocation.controls[0].setValue({
        ...this.allocation.controls[0].value,
        allocationPercentage: 1,
      });

    // Form untouched = even allocation
    if (!this.allocation.dirty) {
      this.allocation.controls[0].setValue({
        ...this.allocation.controls[0].value,
        allocationPercentage: 0,
      });

      //Ignore first control in order to set the missing allocation percent inside
      _.forEach(this.allocation.controls, (c, i) =>
        +i != 0
          ? c.setValue({
              ...c.value,
              allocationPercentage: this.floored(
                1 / +this.allocation.controls.length,
              ),
            })
          : null,
      );

      let allocationLeft =
        this.totalAllocatedPercent < 1
          ? this.floored(1 - this.totalAllocatedPercent)
          : this.floored(this.totalAllocatedPercent - 1);
      this.allocation.controls[0].setValue({
        ...this.allocation.controls[0].value,
        allocationPercentage: allocationLeft,
      });
      return;
    }

    // Allocation < 1 = set value left to the control
    if (this.totalAllocatedPercent < 1) {
      let newControl =
        this.allocation.controls[this.allocation.value.length - 1];
      return newControl.setValue({
        ...newControl.value,
        allocationPercentage: 1 - this.totalAllocatedPercent,
        shareCount: this.allocation.controls[this.allocation.value.length - 1]
          .value.partPrice
          ? Math.round(
              ((1 - this.totalAllocatedPercent / 100) *
                this.allocation.controls[this.allocation.value.length - 1].value
                  .totalInvestedAmount) /
                this.allocation.controls[this.allocation.value.length - 1].value
                  .partPrice,
            )
          : '',
      });
    }
  }

  openSupportsForm() {
    this.supportsForm.open();
    this.formSelectList.loading = true;
    this.search().subscribe(() => {
      this.formSelectList.loading = false;
    });
  }

  onSupportsChange(isData) {
    this.hideTable = isData;
  }

  onMoreInfoClick(support) {
    window.open(`#/dashboard/assets/supports/${support.id}`, '_blank');
  }

  onSubmit() {
    this.displayErrors();

    this.selectErrorInput();

    if (this.form.invalid) {
      this.changeTab.next('FIRST');
      return;
    }

    this.prFormService.loading();
    let formDTOExtraction = this.extract();

    console.log({ formDTOExtraction });

    return this.assetDao.saveAssets(formDTOExtraction).subscribe(
      success => {
        this.notificationService.success('La sauvegarde a réussi');
        this.doneSubject.next();
        if (this.callback) {
          this.callback(this.extract());
        }
      },
      errors => {
        console.error(errors);
        this.notificationService.error('La sauvegarde a échoué');
      },
    );
  }

  onCancel() {
    this.prFormService.close();
  }

  onTabChange(isLastTab: boolean) {
    this.tabChanged.next(isLastTab);
  }

  nextTab() {
    this.changeTab.next('NEXT');
  }

  floored(value) {
    return Math.round(value * 100) / 100;
  }

  get allocation() {
    return this.form.get('allocation') as UntypedFormGroup;
  }

  get totalAllocatedPercent() {
    return this.floored(
      _(this.allocation.value).map('allocationPercentage').sum(),
    );
  }

  get allocationPanel() {
    if (
      !this.allocation ||
      !this.allocation.value ||
      this.allocation.value.length == 0
    )
      return {
        display: 'info',
        icon: 'info',
        text: 'Renseignez vos investissements',
      };
    else if (this.totalAllocatedPercent > 1)
      return {
        display: 'danger',
        icon: 'error',
        text: `${Math.round(
          this.totalAllocatedPercent * 100,
        )}% au total - Vous ne pouvez pas repartir plus de 100% de l'investissement`,
      };
    else if (this.totalAllocatedPercent < 1) {
      // this.form.get('allocation').value[0].totalInvestedAmount is undefined , il n'y a pas de totalInvestedAmount dans value[0]

      return {
        display: 'warning',
        icon: 'warning',
        text: `Il reste ${Math.round(
          this.form.get('allocation').value[0].totalInvestedAmount *
            (1 - this.totalAllocatedPercent),
        )}€ de votre investissement à répartir, soit ${Math.round(
          (1 - this.totalAllocatedPercent) * 100,
        )}%`,
      };
    } else if (
      _.filter(this.allocation.value, scpi => !scpi.allocationPercentage).length
    )
      return {
        display: 'danger',
        icon: 'error',
        text: 'Vous ne pouvez pas repartir 0% dans une SCPI',
      };
    else if (this.totalAllocatedPercent == 1)
      return {
        display: 'success',
        icon: 'check',
        text: 'Félicitations, vos investissements sont répartis à 100%',
      };
  }
}
